import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, RefreshCw, Trash2, ShieldAlert } from "lucide-react";

/* Director/admin only. Sends completed PBPA matches to DUPR (CLUB source). The backend enforces every rule:
   linked + BASIC_L1 players, no ties, no duplicate players, unique identifier, UAT sandbox-only guard. */
export default function DuprMatchPanel({ tournamentId, matches }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/dupr/matches/${tournamentId}`).then(({ data }) => setState(data)).catch(() => setState(null));
  useEffect(() => { load(); }, [tournamentId]);
  if (!state) return null;
  const sent = Object.fromEntries((state.matches || []).filter((m) => m.status !== "deleted").map((m) => [m.match_key, m]));
  const completed = (matches || []).filter((m) => m.status === "complete");
  if (completed.length === 0) return null;
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return (
    <div className="mt-6 border border-pb-border/60 bg-black/30 p-4" data-testid="dupr-match-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-xs font-black uppercase tracking-widest text-zinc-200">DUPR match reporting</h3>
        <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/40 px-1.5" data-testid="dupr-match-env">{state.env} {state.env !== "production" ? "· sandbox players only" : ""}</span>
        {!state.club_id_configured && <span className="inline-flex items-center gap-1 text-[10px] text-red-400" data-testid="dupr-match-noclub"><ShieldAlert className="w-3 h-3" /> DUPR club ID not configured — submissions blocked</span>}
      </div>
      <div className="mt-3 grid gap-1.5" data-testid="dupr-match-list">
        {completed.map((m) => {
          const s = sent[m.key];
          return (
            <div key={m.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] border-b border-pb-border/30 pb-1.5" data-testid={`dupr-match-${m.key}`}>
              <span className="font-mono text-zinc-400 w-24">{m.key}</span>
              <span className="text-zinc-300">{m.score_a}–{m.score_b}</span>
              {s ? <span className="text-[#A3E635]">on DUPR · match {s.match_code} · {s.status}</span> : <span className="text-zinc-600 italic">not sent</span>}
              <span className="ml-auto flex gap-2">
                {!s && <button data-testid={`dupr-submit-${m.key}`} disabled={!!busy} onClick={() => run(m.key, () => api.post(`/dupr/matches/${tournamentId}/${m.key}/submit`), "Sent to DUPR")} className="inline-flex items-center gap-1 text-pb-cyan hover:text-white disabled:opacity-40">{busy === m.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} send</button>}
                {s && <button data-testid={`dupr-update-${m.key}`} disabled={!!busy} onClick={() => run(m.key, () => api.post(`/dupr/matches/${tournamentId}/${m.key}/update`), "DUPR match updated")} className="inline-flex items-center gap-1 text-zinc-300 hover:text-white disabled:opacity-40"><RefreshCw className="w-3 h-3" /> resend</button>}
                {s && <button data-testid={`dupr-delete-${m.key}`} disabled={!!busy} onClick={() => { if (window.confirm("Delete this match from DUPR?")) run(m.key, () => api.delete(`/dupr/matches/${tournamentId}/${m.key}`), "Removed from DUPR"); }} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 disabled:opacity-40"><Trash2 className="w-3 h-3" /> delete</button>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
